import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import axios from "axios";
import { jsPDF } from "jspdf";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import premierLogo from "../../../assessts/premier-logo.png";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import "./InvoicesList.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function InvoiceList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState(location.state?.search || "");
  const [projectNameById, setProjectNameById] = useState({});
  const [clientProjectsByClientId, setClientProjectsByClientId] = useState({});
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [banks, setBanks] = useState([]);
  const [payNowBank, setPayNowBank] = useState({});

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".invdash-filter-container")) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const [payNow, setPayNow] = useState({});
  const [payNowMethod, setPayNowMethod] = useState({});
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [activePayments, setActivePayments] = useState([]);
  const [activeInvoiceNumber, setActiveInvoiceNumber] = useState("");
  const [activeInvoiceCurrency, setActiveInvoiceCurrency] = useState("INR");

  useEffect(() => {
    if (location.state?.search !== undefined) {
      setSearch(location.state.search);
    }
  }, [location.state]);

  const clientId = location.state?.clientId || null;

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        let url = `${API_URL}/api/getAllInvoices`;
        if (clientId) url = `${API_URL}/api/getInvoicesByClient/${clientId}`;

        const res = await axios.get(url);

        if (res.data.success && Array.isArray(res.data.invoices)) {
          const list = res.data.invoices;
          setInvoices(list);

          const defaultAmount = {};
          const defaultMethod = {};
          const defaultBank = {};

          list.forEach((inv) => {
            defaultAmount[inv._id] = "";
            defaultMethod[inv._id] = inv.paymentMethod || "UPI";
            defaultBank[inv._id] = "";
          });

          setPayNow(defaultAmount);
          setPayNowMethod(defaultMethod);
          setPayNowBank(defaultBank);
        } else {
          setInvoices([]);
        }
      } catch {
        setInvoices([]);
      }
    };

    fetchInvoices();
  }, [clientId]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/getProjects`);
        const list = Array.isArray(res.data) ? res.data : [];
        const map = {};
        list.forEach((p) => {
          if (p?._id) map[String(p._id)] = p.projectName || p.name || p.title || "";
        });
        setProjectNameById(map);
      } catch {
        setProjectNameById({});
      }
    };
    const fetchBanks = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/api/bank-account`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) setBanks(res.data.accounts || []);
      } catch (err) {
        setBanks([]);
      }
    };
    fetchProjects();
    fetchBanks();
  }, []);

  useEffect(() => {
    const fetchClientProjects = async () => {
      try {
        const clientIds = Array.from(
          new Set(
            invoices
              .map((inv) => inv?.clientId)
              .filter(Boolean)
              .map((id) => String(id))
          )
        );
        if (!clientIds.length) {
          setClientProjectsByClientId({});
          return;
        }

        const responses = await Promise.all(
          clientIds.map((id) =>
            axios
              .get(`${API_URL}/api/getProjectbyClient/${id}`)
              .then((res) => ({ id, data: Array.isArray(res.data) ? res.data : [] }))
              .catch(() => ({ id, data: [] }))
          )
        );

        const map = {};
        responses.forEach(({ id, data }) => {
          map[id] = data.map((p) => ({
            name: p?.projectName || p?.name || p?.title || "",
            amount: Number(p?.project_price ?? p?.price ?? 0),
          }));
        });
        setClientProjectsByClientId(map);
      } catch {
        setClientProjectsByClientId({});
      }
    };

    fetchClientProjects();
  }, [invoices]);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(premierLogo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoDataUrl(String(reader.result || ""));
        reader.readAsDataURL(blob);
      } catch {
        setLogoDataUrl("");
      }
    };
    loadLogo();
  }, []);

  const visibleInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = invoices;

    if (statusFilter !== "All") {
      filtered = filtered.filter((inv) => {
        return (inv.status || "").toLowerCase() === statusFilter.toLowerCase();
      });
    }

    if (!q) return filtered;

    return filtered.filter((inv) => {
      return (
        String(inv.invoiceNumber || "").toLowerCase().includes(q) ||
        String(inv.clientName || "").toLowerCase().includes(q) ||
        String(inv.status || "").toLowerCase().includes(q)
      );
    });
  }, [invoices, search, statusFilter]);

  const totalRevenue = useMemo(
    () => invoices.reduce((a, b) => a + (b.totalAmount || 0), 0),
    [invoices]
  );

  const pendingCount = useMemo(
    () => invoices.filter((i) => i.status !== "Paid").length,
    [invoices]
  );

  const revenueData = visibleInvoices.map((inv) => ({
    name: inv.invoiceNumber,
    revenue: inv.totalAmount || 0,
  }));

  const statusCount = [
    { name: "Paid", value: visibleInvoices.filter((i) => i.status === "Paid").length },
    { name: "Pending", value: visibleInvoices.filter((i) => i.status === "Pending").length },
    { name: "Partial", value: visibleInvoices.filter((i) => i.status === "Partial").length },
  ];

  const COLORS = ["#8b7a95", "#6d8c86", "#a58b6f"];

  const handleDelete = async (id) => {
    if (!canDo(user, "invoices", "delete")) return alert("No permission");
    if (!window.confirm("Delete this invoice?")) return;

    try {
      await axios.delete(`${API_URL}/api/deleteInvoice/${id}`);
      setInvoices((prev) => prev.filter((inv) => inv._id !== id));
    } catch {
      alert("Delete failed");
    }
  };

  const handleMarkPaid = async (invoice) => {
    if (!canDo(user, "invoices", "edit")) return alert("No permission");

    const amount = Number(payNow[invoice._id]) || 0;
    const bankName = payNowBank[invoice._id];
    
    if (amount <= 0) return alert("Enter valid amount");
    if (!bankName) return alert("Please select a bank");

    try {
      await axios.post(`${API_URL}/api/invoices/${invoice._id}/addPayment`, {
        amount,
        method: payNowMethod[invoice._id] || "UPI",
        bankName: bankName,
        note: "Manual payment",
      });

      setPayNow((prev) => ({ ...prev, [invoice._id]: "" }));

      const res = await axios.get(
        clientId
          ? `${API_URL}/api/getInvoicesByClient/${clientId}`
          : `${API_URL}/api/getAllInvoices`
      );

      setInvoices(res.data.invoices || []);
    } catch {
      alert("Payment failed");
    }
  };

  const handleShowPayments = async (invId, invoiceNumber, currency) => {
    setActiveInvoiceNumber(invoiceNumber || "");
    setActiveInvoiceCurrency(currency || "INR");
    setShowPaymentsModal(true);

    try {
      const res = await axios.get(`${API_URL}/api/getInvoiceById/${invId}`);
      setActivePayments(res.data.invoice?.payments || []);
    } catch {
      setActivePayments([]);
    }
  };

  const handleDownloadPdf = (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const left = 14;
    const right = pageWidth - 14;
    let y = 16;
    const total = Number(invoice.totalAmount || 0);
    const taxAmount = Number(invoice.taxAmount || 0);
    const subTotal =
      invoice.subTotalAmount !== undefined
        ? Number(invoice.subTotalAmount || 0)
        : Math.max(total - taxAmount, 0);
    const paid = Number(invoice.paidAmount || 0);
    const balance = Math.max(total - paid, 0);
    const projects = Array.isArray(invoice.projects) ? invoice.projects : [];
    const invoiceDate = invoice.date ? String(invoice.date).slice(0, 10) : "-";
    const dueDate = invoice.dueDate ? String(invoice.dueDate).slice(0, 10) : "-";

    const currency = invoice.currency || "INR";
    const currSym = { INR: "Rs ", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[currency] || "Rs ";

    doc.setTextColor(36, 118, 194);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", pageWidth / 2, y, { align: "center" });

    y += 14;
    doc.setTextColor(31, 41, 55);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Premier Webtech", left, y);
    doc.setFont("helvetica", "normal");
    doc.text("India", left, y + 8);
    doc.text("Email: support@premierwebtech.com", left, y + 16);
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", right - 48, y - 8, 42, 18);
    }

    y += 22;
    doc.setDrawColor(173, 204, 234);
    doc.line(left, y, right, y);

    y += 10;
    doc.setTextColor(36, 118, 194);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Bill To", left, y);
    doc.setTextColor(31, 41, 55);
    doc.setFont("helvetica", "normal");
    doc.text(String(invoice.clientName || "-"), left, y + 10);

    const labelX = right - 66;
    const valueX = right - 2;
    doc.setFont("helvetica", "bold");
    doc.text("Invoice No :", labelX, y);
    doc.text("Invoice Date :", labelX, y + 10);
    doc.text("Due Date :", labelX, y + 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(String(invoice.invoiceNumber || "-"), valueX, y, { align: "right" });
    doc.text(invoiceDate, valueX, y + 10, { align: "right" });
    doc.text(dueDate, valueX, y + 20, { align: "right" });

    y += 30;
    const rowH = 11;
    const c = {
      sl: left,
      desc: left + 12,
      qty: left + 90,
      rate: left + 116,
      amt: left + 150,
    };
    doc.setFillColor(36, 118, 194);
    doc.rect(left, y, right - left, rowH, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Sl", c.sl + 2, y + 7);
    doc.text("Description", c.desc + 2, y + 7);
    doc.text("Qty", c.qty + 2, y + 7);
    doc.text("Rate", c.rate + 2, y + 7);
    doc.text("Amount", c.amt + 2, y + 7);

    y += rowH;
    const items = projects.length ? projects : [{ name: "Service", amount: total }];
    doc.setTextColor(31, 41, 55);
    doc.setFont("helvetica", "normal");
    items.forEach((p, idx) => {
      const amount = Number(p?.amount || 0);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), c.sl + 2, y + 7);
      doc.text(String(p?.name || "Service"), c.desc + 2, y + 7);
      doc.text("1", c.qty + 2, y + 7);
      doc.text(`${currSym}${amount.toLocaleString()}`, c.rate + 2, y + 7);
      doc.text(`${currSym}${amount.toLocaleString()}`, c.amt + 2, y + 7);
      y += rowH;
    });

    y += 12;
    doc.setTextColor(36, 118, 194);
    doc.setFont("helvetica", "bold");
    doc.text("Payment Instructions", left, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(31, 41, 55);
    doc.text("Pay via Bank / UPI", left, y + 10);
    let infoY = y + 20;
    if (invoice.clientGstNumber) {
      doc.text(`GST Number: ${invoice.clientGstNumber}`, left, infoY);
      infoY += 10;
    }
    if (invoice.clientAccountNo) {
      doc.text(`Account Number: ${invoice.clientAccountNo}`, left, infoY);
      infoY += 10;
    }
    doc.text(`Amount Due: ${currSym}${balance.toLocaleString()}`, left, infoY);

    const boxX = right - 72;
    const boxY = y - 6;
    const boxH = taxAmount > 0 ? 44 : 34;
    doc.setDrawColor(173, 204, 234);
    doc.roundedRect(boxX, boxY, 72, boxH, 2, 2);
    doc.setFont("helvetica", "normal");
    doc.text("Sub Total", boxX + 3, boxY + 8);
    doc.text(`${currSym}${subTotal.toLocaleString()}`, right, boxY + 8, { align: "right" });
    let paidY = boxY + 17;
    if (taxAmount > 0) {
      doc.text(String(invoice.taxName || "Tax"), boxX + 3, boxY + 17);
      doc.text(`${currSym}${taxAmount.toLocaleString()}`, right, boxY + 17, { align: "right" });
      doc.text("Total", boxX + 3, boxY + 26);
      doc.text(`${currSym}${total.toLocaleString()}`, right, boxY + 26, { align: "right" });
      paidY = boxY + 35;
    }
    doc.text("Paid", boxX + 3, paidY);
    doc.text(`${currSym}${paid.toLocaleString()}`, right, paidY, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text("Balance", boxX + 3, paidY + 10);
    doc.text(`${currSym}${balance.toLocaleString()}`, right, paidY + 10, { align: "right" });

    y += 54;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(31, 41, 55);
    doc.text(`Payment Status: ${invoice.status || "-"}`, left, y);
    
    if (invoice.payments && invoice.payments.length > 0) {
      y += 12;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(36, 118, 194);
      doc.text("Payment Details", left, y);
      y += 8;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(31, 41, 55);
      invoice.payments.forEach((payment) => {
         const pDate = payment.date ? String(payment.date).slice(0, 10) : "-";
         const pBank = payment.bankName || "-";
         const pMethod = payment.method || "-";
         const pAmt = `${currSym}${Number(payment.amount || 0).toLocaleString()}`;
         doc.text(`Date: ${pDate}  |  Bank: ${pBank}  |  Method: ${pMethod}  |  Amount: ${pAmt}`, left + 4, y);
         y += 6;
      });
    }

    const safeName = String(invoice.clientName || "client").replace(/[^\w-]/g, "_");
    doc.save(`${safeName}_invoice.pdf`);
  };

  const exportExcel = () => {
    const rows = visibleInvoices.map((inv, i) => {
      const paid = Number(inv.paidAmount || 0);
      const total = Number(inv.totalAmount || 0);
      const unpaid = Math.max(total - paid, 0);

      const projectNames =
        Array.isArray(inv.projects) && inv.projects.length > 0
          ? inv.projects
            .map((p) => {
              const projectId =
                typeof p?.projectId === "object" ? p.projectId?._id : p?.projectId;
              const amount = Number(p?.amount || 0);
              const clientProjects = clientProjectsByClientId[String(inv?.clientId)] || [];
              const amountMatches = clientProjects.filter((cp) => Number(cp.amount || 0) === amount);
              const matchedByAmount = amountMatches.length === 1 ? amountMatches[0].name : "";
              const singleProjectFallback =
                clientProjects.length === 1 ? clientProjects[0]?.name || "" : "";
              return (
                p?.projectName ||
                p?.name ||
                p?.project_title ||
                p?.project_name ||
                p?.title ||
                (typeof p?.projectId === "object" ? p.projectId?.projectName : "") ||
                (projectId ? projectNameById[String(projectId)] : "") ||
                matchedByAmount ||
                singleProjectFallback ||
                "Unnamed Project"
              );
            })
            .join(", ")
          : "";

      const projectCell = projectNames ? `${inv.projects.length} (${projectNames})` : "0";
      return {
        S_No: i + 1,
        Invoice: inv.invoiceNumber || "-",
        Client: inv.clientName || "-",
        No_of_Projects: projectCell,
        Total: total,
        Paid: paid,
        Unpaid: unpaid,
        Status: inv.status || "-",
        Due: inv.dueDate ? String(inv.dueDate).slice(0, 10) : "-",
        Created: inv.date ? String(inv.date).slice(0, 10) : "-",
      };
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Invoices");
    XLSX.writeFile(wb, "Invoices.xlsx");
  };

  if (!canViewPage(user, "invoices")) {
    return <div className="text-center mt-5 text-danger">Access Denied</div>;
  }

  return (
    <div className="invdash-page">
      <section className="invdash-hero">
        <div className="invdash-hero-left">
          <p className="invdash-overline">Finance</p>
          <div className="invdash-title-row">
            <h4>Invoice Dashboard</h4>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice / client / status"
              className="invdash-search"
            />
            <div className="invdash-filter-container">
              <button
                className={`invdash-filter-toggle-btn ${statusFilter !== "All" ? "active" : ""}`}
                type="button"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '6px' }}>
                  <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.124.325L9 8.3v5.5a.5.5 0 0 1-.276.447l-2 1A.5.5 0 0 1 6 15V8.3L1.624 3.825A.5.5 0 0 1 1.5 3.5v-2z" />
                </svg>
                <span>Filter: {statusFilter}</span>
              </button>
              {showFilterDropdown && (
                <div className="invdash-filter-dropdown">
                  {["All", "Paid", "Partial", "Pending"].map((status) => (
                    <div
                      key={status}
                      className={`invdash-filter-option ${statusFilter === status ? "selected" : ""}`}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilterDropdown(false);
                      }}
                    >
                      {status}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="invdash-subline">Track billing, payment flow, and invoice health</p>
        </div>

        <div className="invdash-hero-actions">
          <button className="invdash-btn ghost" onClick={() => navigate("/admin/manual-invoice")}>
            Add Invoice
          </button>
          <button className="invdash-btn ghost" onClick={exportExcel}>
            Export Excel
          </button>
        </div>
      </section>

      <section className="invdash-kpi-grid">
        <article className="invdash-kpi-card">
          <p>Total Invoices</p>
          <h3>{invoices.length}</h3>
        </article>

        <article className="invdash-kpi-card">
          <p>Total Revenue</p>
          <h3>Rs {totalRevenue.toLocaleString()}</h3>
        </article>

        <article className="invdash-kpi-card">
          <p>Pending</p>
          <h3>{pendingCount}</h3>
        </article>
      </section>

      <section className="invdash-chart-grid">
        <article className="invdash-chart-card">
          <h6>Revenue Trend</h6>
          <div className="invdash-chart-box">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={revenueData}>
                <XAxis dataKey="name" stroke="#000000" tick={{ fill: "#000000", fontSize: 12 }} />
                <YAxis stroke="#000000" tick={{ fill: "#000000", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid rgba(148,163,184,0.3)",
                    borderRadius: "10px",
                    color: "#000000",
                  }}
                />
                <Line type="monotone" dataKey="revenue" stroke="#c8a46f" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="invdash-chart-card">
          <h6>Invoice Status</h6>
          <div className="invdash-chart-box">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={statusCount} dataKey="value" outerRadius={90}>
                  {statusCount.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  wrapperStyle={{ color: "#000000" }}
                  formatter={(value) => <span style={{ color: "#000000" }}>{value}</span>}
                />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid rgba(148,163,184,0.3)",
                    borderRadius: "10px",
                    color: "#000000",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      <section className="invdash-table-card">
        <div className="invdash-table-wrap">
          <table className="table align-middle text-center mb-0 invdash-table">
            <thead>
              <tr>
                {["#", "Invoice", "Client", "No. of Projects", "Total", "Paid", "Unpaid", "Payments", "Pay Now", "Status", "Due", "Created", "Actions"].map(
                  (h, i) => (
                    <th key={i}>{h}</th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {visibleInvoices.map((invoice, idx) => {
                const paid = invoice.paidAmount || 0;
                const unpaid = (invoice.totalAmount || 0) - paid;

                return (
                  <tr key={invoice._id}>
                    <td>{idx + 1}</td>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.clientName}</td>
                    <td>
                      {Array.isArray(invoice.projects) && invoice.projects.length > 0
                        ? `${invoice.projects.length} (${invoice.projects
                          .map((p) => {
                            const projectId =
                              typeof p?.projectId === "object" ? p.projectId?._id : p?.projectId;
                            const amount = Number(p?.amount || 0);
                            const clientProjects = clientProjectsByClientId[String(invoice?.clientId)] || [];
                            const amountMatches = clientProjects.filter((cp) => Number(cp.amount || 0) === amount);
                            const matchedByAmount = amountMatches.length === 1 ? amountMatches[0].name : "";
                            const singleProjectFallback =
                              clientProjects.length === 1 ? clientProjects[0]?.name || "" : "";
                            const projectLabel =
                              p?.projectName ||
                              p?.name ||
                              p?.project_title ||
                              p?.project_name ||
                              p?.title ||
                              (typeof p?.projectId === "object" ? p.projectId?.projectName : "") ||
                              (projectId ? projectNameById[String(projectId)] : "") ||
                              matchedByAmount ||
                              singleProjectFallback ||
                              "Unnamed Project";
                            return projectLabel;
                          })
                          .join(", ")})`
                        : "0"}
                    </td>
                    <td>{({ INR: "Rs ", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[invoice.currency] || "Rs ")}{Number(invoice.totalAmount || 0).toLocaleString()}</td>
                    <td>{({ INR: "Rs ", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[invoice.currency] || "Rs ")}{paid.toLocaleString()}</td>
                    <td>{({ INR: "Rs ", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[invoice.currency] || "Rs ")}{unpaid.toLocaleString()}</td>

                    <td>
                      <button
                        className="invdash-mini-btn"
                        onClick={() => handleShowPayments(invoice._id, invoice.invoiceNumber, invoice.currency)}
                      >
                        History
                      </button>
                    </td>

                    <td>
                      <div className="d-flex flex-column gap-1" style={{ width: '130px' }}>
                        <select 
                          className="form-select form-select-sm"
                          value={payNowBank[invoice._id] || ""}
                          onChange={(e) => setPayNowBank({...payNowBank, [invoice._id]: e.target.value})}
                        >
                          <option value="">Select Bank</option>
                          {banks.map(b => (
                            <option key={b._id} value={b.bankName}>{b.bankName}</option>
                          ))}
                        </select>
                        <select 
                          className="form-select form-select-sm"
                          value={payNowMethod[invoice._id] || "UPI"}
                          onChange={(e) => setPayNowMethod({...payNowMethod, [invoice._id]: e.target.value})}
                        >
                          <option value="UPI">UPI</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Net Banking">Net Banking</option>
                          <option value="Cash">Cash</option>
                          <option value="Card">Card</option>
                          <option value="Debit Card">Debit Card</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Online">Online</option>
                        </select>
                        <div className="input-group input-group-sm">
                          <input
                            className="form-control"
                            value={payNow[invoice._id]}
                            onChange={(e) =>
                              setPayNow({ ...payNow, [invoice._id]: e.target.value })
                            }
                            placeholder="Amount"
                          />
                          <button className="btn btn-primary" onClick={() => handleMarkPaid(invoice)}>
                            Add
                          </button>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className={`invdash-status ${String(invoice.status || "").toLowerCase()}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>{invoice.dueDate ? String(invoice.dueDate).slice(0, 10) : "-"}</td>
                    <td>{invoice.date ? String(invoice.date).slice(0, 10) : "-"}</td>

                    <td>
                      <div className="invdash-action-wrap">
                        <button className="invdash-mini-btn" onClick={() => handleDownloadPdf(invoice)}>
                          Download PDF
                        </button>
                        <button className="invdash-mini-btn" onClick={() => navigate(`/admin/viewInvoice/${invoice._id}`)}>
                          View
                        </button>
                        <button className="invdash-mini-btn danger" onClick={() => handleDelete(invoice._id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {showPaymentsModal ? (
        <div className="invdash-modal-backdrop" onClick={() => setShowPaymentsModal(false)}>
          <div className="invdash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invdash-modal-head">
              <h6>Payments - {activeInvoiceNumber}</h6>
              <button className="invdash-mini-btn" onClick={() => setShowPaymentsModal(false)}>
                Close
              </button>
            </div>
            <div className="invdash-modal-body">
              {activePayments.length === 0 ? (
                <p className="mb-0 text-muted">No payment history found.</p>
              ) : (
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Amount</th>
                      <th>Bank</th>
                      <th>Method</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activePayments.map((p, i) => (
                      <tr key={i}>
                        <td>{({ INR: "Rs ", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[activeInvoiceCurrency] || "Rs ")}{Number(p.amount || 0).toLocaleString()}</td>
                        <td>{p.bankName || "-"}</td>
                        <td>{p.method || "-"}</td>
                        <td>{p.date ? new Date(p.date).toLocaleString() : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default InvoiceList;
